/*
  This ended up being a mixin, instead of a component/wrapper, due to issues with JustiFi's web component.
  If you include this mixin, here are the steps to take.

  1. Make sure the props for this mix get passed into your component.
  2. Include markup, like is shown in update, in your template html.
     This example is from update_cc.vue, which uses the more modern validationproviders
     but it could be simpler for Justifi.
                   <div class="form-group">
                <label for="address_2" class="font-normal">
                  Address line 2 (optional)
                </label>
                <input autocomplete="address-line2" v-model="addressLine2" id="address_2" type="text"
                       class="form-control">
              </div>
              <div class="form-group">
                <ValidationProvider name="city" rules="required" v-slot="{ errors }">
                  <label for="locality-input" class="font-normal">
                    City
                  </label>
                  <input autocomplete="address-level2" v-model="city" id="locality-input" type="text" ref="city"
                         :class="inputClasses(errors[0])">
                  <p class="m-t-xxs font-small text-red-600" v-if="errors[0]">
                    {{ errors[0] }}
                  </p>
                </ValidationProvider>
              </div>
              <div class="form-group">
                <div class="row">
                  <div class="col-xs-12 col-sm-6 m-b-only-xs-m">
                    <ValidationProvider name="state" rules="required" v-slot="{ errors }">
                      <label for="administrative_area_level_1-input" class="font-normal">
                        State
                      </label>
                      <the-mask
                          v-model="state"
                          mask="AA"
                          type="text"
                          :class="inputClasses(errors[0])"
                          ref="state"
                          autocomplete="address-level2"
                          id="administrative_area_level_1-input"
                      ></the-mask>
                      <p class="m-t-xxs font-small text-red-600" v-if="errors[0]">
                        {{ errors[0] }}
                      </p>
                    </ValidationProvider>
                  </div>
                  <div class="col-xs-12 col-sm-6 m-b-only-xs-m">
                    <ValidationProvider name="zip" rules="required" v-slot="{ errors }">
                      <label for="postal_code-input" class="font-normal">
                        Zip
                      </label>
                      <the-mask
                          v-model="zip"
                          mask="#####"
                          type="tel"
                          autocomplete="postal-code"
                          :class="inputClasses(errors[0])"
                          ref="zip"
                          id="postal_code-input"
                      ></the-mask>
                      <p class="m-t-xxs font-small text-red-600" v-if="errors[0]">
                        {{ errors[0] }}
                      </p>
                    </ValidationProvider>
                  </div>
                </div>
              </div>
              <div class="form-group">
                <ValidationProvider name="country" rules="required" v-slot="{ errors }">
                  <label for="country-input" class="font-normal">
                    Country
                  </label>
                  <select v-model="country" class="form-control">
                    <option selected disabled></option>
                    <option v-for="country in countries" :value="country.short_name" id="country-input">{{
                        country.long_name
                      }}
                    </option>
                  </select>
                  <p class="m-t-xxs font-small text-red-600" v-if="errors[0]">
                    {{ errors[0] }}
                  </p>
                </ValidationProvider>
              </div>
    3. At some point, you will need to call setupPaymentElement with a setup/payment intent, preferably with forceNextTick. This will render the payment form.
    4. When the button is pressed to submit payment, call getPaymentToken.
    5. Make sure you have implemented a tokenized(token) method on your vue component to receive the token and fire it off.
 */
import * as Sentry from '@sentry/vue';
import { cardLogoMix } from './card_logo_mix.js';

// JustiFi tokenize failures fall into three buckets for Sentry (see reportJustifiTokenizeFailure):
// - Bank declines: the issuer said no to this card. Nothing we can act on, so not reported.
// - Card details: JustiFi rejected what was typed. Usually the parent, but a spike means the form
//   is letting bad input through (e.g. card_number_invalid passing our validate()), so keep them.
// - Platform: everything else, including codes we've never seen. These are ours or JustiFi's to fix
//   (expired tokens, network failures, rate limits, disabled accounts) and are reported as errors.
const JUSTIFI_BANK_DECLINE_CODES = ['card_declined', 'expired_card'];
const JUSTIFI_CARD_DETAIL_CODES = [
  'card_number_invalid', 'card_number_required', 'card_number_error', 'month_invalid',
  'two_digit_year_required', 'verification_invalid', 'generic_decline',
];

export const billingPartnerMix = {
  mixins: [cardLogoMix],
  props: {
    billingPartner: {
      type: String,
      required: true,
      validator(value) {
        // The value must match one of these strings
        return ['stripe', 'justifi'].includes(value);
      },
    },
    billingAcctId: {
      type: String,
    },
    billingKey: {
      type: String,
    },
    billingIntentSecret: {
      type: String,
    },
    teamName: {
      type: String,
    },
    teamCurrency: {
      type: String,
      default: 'usd',
    },
    billingProfile: {
      type: Object,
      required: true,
    },
    // We will pre-fill based on address questions earlier in the flow
    // assuming this is the first time someone comes through and we
    // don't already have saved billing info
    bestGuessBillingInfo: {
      type: Object,
      default() { return {}; },
    },
    useGoogleAutocomplete: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      billingPartnerId: null,
      billingPartnerKey: null,
      stripe: null,
      intentSecret: null,
      creatingToken: false,
      apiKey: null,
      justifiLoading: true,
      // JustiFi specific stuff for the card form
      nameOnCard: '',
      addressLine1: '',
      addressLine2: '',
      city: '',
      state: '',
      zip: '',
      country: 'US',
      countries: [
        { long_name: 'Australia', short_name: 'AU' },
        { long_name: 'Belgium', short_name: 'BE' },
        { long_name: 'Brazil', short_name: 'BR' },
        { long_name: 'Bulgaria', short_name: 'BG' },
        { long_name: 'Canada', short_name: 'CA' },
        { long_name: 'China', short_name: 'CN' },
        { long_name: 'Cyprus', short_name: 'CY' },
        { long_name: 'Czechia', short_name: 'CZ' },
        { long_name: 'Denmark', short_name: 'DK' },
        { long_name: 'Estonia', short_name: 'EE' },
        { long_name: 'Finland', short_name: 'FI' },
        { long_name: 'France', short_name: 'FR' },
        { long_name: 'Germany', short_name: 'DE' },
        { long_name: 'Greece', short_name: 'GR' },
        { long_name: 'Hong Kong SAR China', short_name: 'HK' },
        { long_name: 'India', short_name: 'IN' },
        { long_name: 'Ireland', short_name: 'IE' },
        { long_name: 'Italy', short_name: 'IT' },
        { long_name: 'Japan', short_name: 'JP' },
        { long_name: 'Latvia', short_name: 'LV' },
        { long_name: 'Lithuania', short_name: 'LT' },
        { long_name: 'Luxembourg', short_name: 'LU' },
        { long_name: 'Malaysia', short_name: 'MY' },
        { long_name: 'Malta', short_name: 'MT' },
        { long_name: 'Mexico', short_name: 'MX' },
        { long_name: 'Netherlands', short_name: 'NL' },
        { long_name: 'New Zealand', short_name: 'NZ' },
        { long_name: 'Norway', short_name: 'NO' },
        { long_name: 'Poland', short_name: 'PL' },
        { long_name: 'Portugal', short_name: 'PT' },
        { long_name: 'Romania', short_name: 'RO' },
        { long_name: 'Singapore', short_name: 'SG' },
        { long_name: 'Slovakia', short_name: 'SK' },
        { long_name: 'Slovenia', short_name: 'SI' },
        { long_name: 'Spain', short_name: 'ES' },
        { long_name: 'Sweden', short_name: 'SE' },
        { long_name: 'Switzerland', short_name: 'CH' },
        { long_name: 'United Kingdom', short_name: 'GB' },
        { long_name: 'United States', short_name: 'US' },
      ],
      reusePaymentMethodInFuture: true,
      email: '',
      addressAutocomplete: null,
      idDataMap: {
        'location-input': 'addressLine1',
        'locality-input': 'city',
        'administrative_area_level_1-input': 'state',
        'postal_code-input': 'zip',
        'country-input': 'country',
      },
      // Justifi intent expiration tracking
      paymentExpirationTimer: null,
      paymentStartTime: Date.now(),
      cartExpirationInMinutes: 57, // Technically 60, but we want to account for the time it takes to submit the form too.
      timeRemaining: 57 * 60,
      paymentExpired: false,
      // Logged-in pages swap in a fresh token in place (POST /api/v1/justifi_tokens) when a
      // submit comes back not_authenticated, instead of reloading. A 4xx there flips this on and
      // the parent is told to refresh the page.
      justifiTokenRefreshUnavailable: false,
      justifiTokenRefresh: null, // the in-flight refresh request, shared by every caller
      signedInAsUser: false,
    };
  },
  mounted() {
    // Although axios is already setup with this, the BillingPartnerMix needs it explicitly
    // If we ever embed this elsewhere, it will need to change.
    this.apiKey = document.querySelector('meta[name=ax-team-token]')?.content;
    this.billingPartnerId = this.billingAcctId;
    this.billingPartnerKey = this.billingKey;
    this.intentSecret = this.billingIntentSecret;
    // shared/_axios_setup marks pages rendered for a signed-in user (token blank when nobody is)
    this.signedInAsUser = document.querySelector('meta[name=ax-auth-type]')?.content === 'user'
      && Boolean(document.querySelector('meta[name=ax-auth-token]')?.content);
    // A server-rendered token starts its 60 minute clock at page render, and some JustiFi forms
    // (membership_form, update_cc) never call setupPaymentElement, so start tracking it here.
    if (this.useJustifi && this.intentSecret) {
      this.startExpirationTimer(false);
    }
    this.email = this.billingProfile?.email;
    this.fetchBillingAcctInfo();

    // Saved billing profile info, if the user saved it before
    if (this.billingProfile?.default_payment_method_reusable) {
      this.nameOnCard = this.billingProfile.name_on_card;
      this.addressLine1 = this.billingProfile.address_line_1;
      this.addressLine2 = this.billingProfile.address_line_2;
      this.city = this.billingProfile.city;
      this.state = this.billingProfile.state;
      this.zip = this.billingProfile.zip;
      this.country = this.billingProfile.country;
    } else if (this.hasGuessBillingInfo) {
      // In this situation we are preloading the billing info from an address question previously asked
      this.nameOnCard = this.bestGuessBillingInfo.name_on_card;
      this.addressLine1 = this.bestGuessBillingInfo.address_line_1;
      this.addressLine2 = this.bestGuessBillingInfo.address_line_2;
      this.city = this.bestGuessBillingInfo.city;
      this.state = this.bestGuessBillingInfo.state;
      this.zip = this.bestGuessBillingInfo.zip;
    } else {
      // If we have no best guess, we only prefill the country based on the teams currency.
      // We currencly prefill USD to US, CAD to CA and NZD to NZ
      switch (this.teamCurrency) {
        case 'cad':
          this.country = 'CA';
          break;
        case 'nzd':
          this.country = 'NZ';
          break;
        default:
          this.country = 'US';
          break;
      }
    }

    this.initGoogleAutocomplete();
  },
  beforeDestroy() {
    if (this.addressAutocomplete) {
      google.maps.event.clearInstanceListeners(this.addressAutocomplete);
      this.addressAutocomplete = null;
    }
    // Clean up timer when component is destroyed
    if (this.paymentExpirationTimer) {
      clearInterval(this.paymentExpirationTimer);
    }
  },
  computed: {
    // Calculate minutes and seconds remaining
    minutesRemaining() {
      return Math.floor(this.timeRemaining / 60);
    },
    secondsRemaining() {
      return this.timeRemaining % 60;
    },
    formattedTimeRemaining() {
      const mins = this.minutesRemaining;
      const secs = this.secondsRemaining.toString().padStart(2, '0');
      return `${mins}:${secs}`;
    },
    showExpirationWarning() {
      // Show warning when 15 minutes or less remain
      return !this.justifiTokenRefreshable && this.timeRemaining <= 15 * 60
        && this.timeRemaining > 0 && !this.paymentExpired;
    },
    // A signed-in page tokenizing against a team's own JustiFi account gets a fresh token on submit
    // (POST /api/v1/justifi_tokens), so its form never really expires and needs no countdown.
    // Anonymous pages (401) and team billing on the WIQ platform account (mismatch) keep it.
    justifiTokenRefreshable() {
      return this.useJustifi && this.signedInAsUser
        && (this.billingPartnerId || '').startsWith('acc_');
    },
    hasGuessBillingInfo() {
      return Object.keys(this.bestGuessBillingInfo).length !== 0;
    },
    hasSavedCard() {
      return this.billingProfile.default_payment_method_id && this.billingProfile.default_payment_method_reusable;
    },
    useStripe() {
      return this.billingPartner === 'stripe';
    },
    useJustifi() {
      return this.billingPartner === 'justifi';
    },
    cardLogo() {
      switch (this.billingProfile.default_payment_method_brand) {
        case 'visa':
          return this.visaLogo;
        case 'mastercard':
          return this.mastercardLogo;
        case 'amex':
        case 'american_express':
          return this.amexLogo;
        case 'discover':
          return this.discoverLogo;
        default:
          return `<div>${this.billingProfile.default_payment_method_brand}</div>`;
      }
    },
    isUSA() {
      return this.country === 'US';
    },
  },
  methods: {
    reloadPageForExpiration() {
      window.location.reload();
    },
    startExpirationTimer(resetStartTime) {
      // Clear any existing timer
      if (this.paymentExpirationTimer) {
        clearInterval(this.paymentExpirationTimer);
      }

      // No countdown where an expired token is swapped on submit. Refreshing ahead of time instead
      // kept an idle tab minting a token every 37 minutes for as long as it stayed open.
      if (this.justifiTokenRefreshable) {
        this.paymentExpired = false;
        return;
      }

      // in some circumstances we do not want to reset the timer, such as when the client secret is server side
      if (resetStartTime) {
        this.paymentStartTime = Date.now();
      }
      this.timeRemaining = this.cartExpirationInMinutes * 60; // Reset to cart timer to avoid race conditions
      this.paymentExpired = false;

      // Update timer every second
      this.paymentExpirationTimer = setInterval(() => {
        const elapsed = Math.floor((Date.now() - this.paymentStartTime) / 1000);
        this.timeRemaining = Math.max(0, (this.cartExpirationInMinutes * 60) - elapsed);

        // Check if expired
        if (this.timeRemaining === 0) {
          this.paymentExpired = true;
          clearInterval(this.paymentExpirationTimer);
        }
      }, 1000);
    },
    initGoogleAutocomplete() {
      if (!this.useGoogleAutocomplete) {
        return;
      }

      // Setup Google Autocomplete
      const vm = this;
      try {
        const input = document.getElementById('location-input');
        // Skip autocomplete if we don't have this element in the DOM
        if (!input) {
          return;
        }
        // Create the autocomplete object
        this.addressAutocomplete = new google.maps.places.Autocomplete(input, {
          fields: ['address_components', 'geometry', 'name'],
          types: ['address'],
        });

        // Add a listener for when a place is selected
        this.addressAutocomplete.addListener('place_changed', () => {
          const place = vm.addressAutocomplete.getPlace();
          if (!place.geometry) {
            console.log(`No details available or autocomplete skipped for input: '${place.name}'`);
            return;
          }

          // Fill in the address from the autocomplete object
          vm.fillInAddress(place);
        });
      } catch (error) {
        console.log('autocomplete error');
        console.log(error);
        vm.handleAutocompleteError();
      }

      // Fallback to ensure the form is not disabled if the autocomplete fails
      // Fallback to ensure input is always editable
      setInterval(() => {
        const input = document.getElementById('location-input');
        if (input && (input.hasAttribute('readonly') || input.disabled)) {
          vm.handleAutocompleteError();
        }
      }, 1000);
    },
    // Google js library locks up the input if it errors, so we are very careful to make sure it doesn't happen
    handleAutocompleteError() {
      const element = document.getElementById('location-input');
      if (!element) {
        return;
      }

      element.removeAttribute('disabled');
      element.removeAttribute('readonly');
      element.style.cssText = '';
      element.placeholder = '';
    },
    // Autocomplete handling from Google Places API
    fillInAddress(place) {
      const SHORT_NAME_ADDRESS_COMPONENT_TYPES = new Set(['street_number', 'administrative_area_level_1', 'postal_code', 'country']);

      const ADDRESS_COMPONENT_TYPES_IN_FORM = [
        'location',
        'locality',
        'administrative_area_level_1',
        'postal_code',
        'country',
      ];

      function getComponentName(componentType) {
        for (const component of place.address_components || []) {
          if (component.types[0] === componentType) {
            return SHORT_NAME_ADDRESS_COMPONENT_TYPES.has(componentType)
              ? component.short_name
              : component.long_name;
          }
        }
        return '';
      }

      function getComponentText(componentType) {
        return (componentType === 'location')
          ? `${getComponentName('street_number')} ${getComponentName('route')}`
          : getComponentName(componentType);
      }

      for (const componentType of ADDRESS_COMPONENT_TYPES_IN_FORM) {
        // Step 1: Identify the Vue data property
        const dataProperty = this.idDataMap[`${componentType}-input`];
        this[dataProperty] = getComponentText(componentType);
      }
    },
    clearBillingInfo() {
      this.nameOnCard = '';
      this.addressLine1 = '';
      this.addressLine2 = '';
      this.city = '';
      this.state = '';
      this.zip = '';
      this.country = 'US';
    },
    // In some situations, we remotely retrieve this (like embedding)
    fetchBillingAcctInfo() {
      // If we already have this info, just init everything
      if (this.billingPartnerId != null && this.billingPartnerKey != null) {
        if (this.useStripe) {
          if (this.billingPartnerId === 'wiq_platform') {
            // Just use our platform billing system
            this.stripe = Stripe(this.billingPartnerKey);
          } else {
            // Use Stripe Connect
            this.stripe = Stripe(this.billingPartnerKey, {
              stripeAccount: this.billingPartnerId,
            });
          }
        }
      } else {
        // If we can't bootstrap ourselves, attempt to remotely fetch details to bootstrap
        const vm = this;
        const url = `/api/team/v1/account/${vm.billingPartner}/${vm.apiKey}`;
        axios.get(url)
          .then((response) => {
            vm.billingPartnerId = response.data.billing_partner.acct_id;
            vm.billingPartnerKey = response.data.billing_partner.platform_key;
            if (vm.useStripe) {
              vm.stripe = Stripe(vm.billingPartnerKey, {
                stripeAccount: vm.billingPartnerId,
              });
            }
          })
          .catch((error) => {
            console.error('Unable to get Stripe info for team');
            console.error(error);
          });
      }
    },
    // primary color, text color, and useInter are all optional params
    setupPaymentElement(intentSecret, primaryColor, textColor, useInter) {
      if (this.useJustifi) {
        const secretIsDifferent = intentSecret !== this.intentSecret;
        this.intentSecret = intentSecret;
        this.startExpirationTimer(secretIsDifferent);
      } else if (this.useStripe) {
        const vm = this;
        vm.intentSecret = intentSecret;

        const colorPrimary = primaryColor || '#27AA83';
        const colorText = textColor || '#324D67';
        // We don't use the variable version in this case as I'm not sure it's working
        const fontFamily = useInter ? 'Inter, Open Sans, system-ui, sans-serif' : 'Open Sans, system-ui, sans-serif';
        const cssSrc = useInter ? 'https://fonts.googleapis.com/css?family=Inter' : 'https://fonts.googleapis.com/css?family=Open+Sans';
        const options = {
          clientSecret: vm.intentSecret,
          fonts: [{
            cssSrc,
          }],
          appearance: {
            theme: 'stripe',
            variables: {
              colorPrimary,
              colorText,
              colorDanger: '#BB2525',
              fontFamily,
              fontWeightNormal: '600',
            },
          },
        };
        vm.elements = vm.stripe.elements(options);

        // Mount the form
        const paymentElement = vm.elements.create('payment', {
          business: {
            name: vm.teamName,
          },
          terms: {
            card: 'never',
          },
        });
        paymentElement.mount('#payment-element');
      }
    },
    // What callers should call
    // We expect a tokenized method to exist on the component that the mix is included with.
    getPaymentToken() {
      if (this.useStripe) {
        this.getStripeToken();
      } else if (this.useJustifi) {
        this.getJustifiToken();
      }
    },
    // Private for Stripe
    async getStripeToken() {
      const vm = this;

      if (vm.creatingToken) {
        return;
      }

      vm.clearError();
      vm.creatingToken = true;
      const { elements } = vm;

      const { error } = await vm.stripe.confirmSetup({
        // `Elements` instance that was used to create the Payment Element
        elements,
        confirmParams: {
          return_url: window.location.href,
        },
        redirect: 'if_required',
      });

      if (error) {
        // This point will only be reached if there is an immediate error when
        // confirming the payment. Show error to your customer (for example, payment
        // details incomplete)
        vm.errorMessage = error.message;
        vm.error = true;
        vm.creatingToken = false;
      } else {
        // Usually the promise resolves straight to the return_url, but because of our redirect_if flag,
        // we handle success inline here.
        // Now we need to retrieve the intent to pass it into the endpoint so we know which payment method to charge
        // For some reason this api call is not working with async/await so doing old school calls
        vm.stripe.retrieveSetupIntent(vm.intentSecret).then((result) => {
          vm.creatingToken = false;
          if (result.error) {
            vm.showError(result.error.message);
          } else {
            vm.tokenized(result.setupIntent.payment_method);
          }
        });
      }
    },
    // Private for Justifi
    getJustifiToken() {
      this.error = false;
      this.creatingToken = true;

      const justiForm = document.querySelector('justifi-tokenize-payment-method');
      justiForm.validate()
        .then((data) => {
          console.log('justifi-card-form validated. Is valid? ', data.isValid);
          if (!data.isValid) {
            this.errorMessage = 'Please correct the errors shown above.';
            this.error = true;
            this.creatingToken = false;
            return;
          }

          this.error = false;
          this.tokenizeJustifiPaymentMethod(justiForm, true);
        });
    },
    tokenizeJustifiPaymentMethod(justiForm, retryWithFreshToken) {
      justiForm.tokenizePaymentMethod()
        .then(async (response) => {
          // This is where you can submit the form and use the payment method token
          // on your backend
          console.log('justifi-card-form tokenized: ', response);
          if (response.error) {
            // The web component token lapsed (the page sat open past ~60 minutes). The component
            // reads auth-token on every tokenize call, so a fresh one can be swapped in without
            // losing the card the parent already typed.
            const expired = response.error.code === 'not_authenticated';
            if (retryWithFreshToken && expired && await this.refreshJustifiToken()) {
              this.tokenizeJustifiPaymentMethod(justiForm, false);
              return;
            }

            this.creatingToken = false;
            this.errorMessage = this.justifiTokenizeErrorMessage(response.error);
            this.error = true;
            this.reportJustifiTokenizeFailure(response.error, 'response');
            return;
          }
          this.creatingToken = false;
          const { data } = response;
          this.tokenized(data.card.token);
        })
        .catch((error) => {
          // But this never throws?
          this.creatingToken = false;
          this.errorMessage = `Unknown Error ${error}`;
          this.error = true;
          this.reportJustifiTokenizeFailure(error, 'rejected');
        });
    },
    // Resolves true once a fresh token is bound to <justifi-tokenize-payment-method>, false if we
    // can't get one (anonymous pages have no logged-in profile, so /api/v1 answers 401).
    refreshJustifiToken() {
      // A background refresh and a submit retry can overlap; share one request
      if (!this.justifiTokenRefresh) {
        this.justifiTokenRefresh = this.requestFreshJustifiToken()
          .finally(() => { this.justifiTokenRefresh = null; });
      }
      return this.justifiTokenRefresh;
    },
    async requestFreshJustifiToken() {
      if (this.justifiTokenRefreshUnavailable) {
        return false;
      }

      try {
        const response = await axios.post('/api/v1/justifi_tokens', { account_id: this.billingAcctId });
        this.intentSecret = response.data.token;
        this.startExpirationTimer(true);
        // Let Vue push the new auth-token attribute onto the web component before anyone tokenizes
        await this.$nextTick();
        return true;
      } catch (error) {
        console.warn('Unable to refresh JustiFi token', error);
        // A 4xx means this page can't mint tokens (anonymous, or a non-team account), so stop.
        // Network blips stay retryable for the not_authenticated path on submit.
        const status = error.response?.status;
        if (status && status < 500) {
          this.justifiTokenRefreshUnavailable = true;
        }
        // 401 is the expected answer on anonymous pages; anything else means the refresh itself is broken
        if (status !== 401) {
          Sentry.withScope((scope) => {
            scope.setTag('justifi_failure_kind', 'platform');
            Sentry.captureMessage(`JustiFi token refresh failed: ${status ?? error.message}`, 'error');
          });
        }
        return false;
      }
    },
    // JustiFi's hosted iframe does `return await resp.json()` inside a try, and its catch hands the raw
    // exception back as `error`. When tokenize.justifi.ai answers with something that is not JSON
    // (an edge error page, a captive portal, a content blocker) Safari throws a DOMException with
    // code 12 / name SyntaxError and the message "The string did not match the expected pattern".
    // That is a connectivity problem, not a card problem, so say so instead of echoing the DOMException.
    justifiTokenizeErrorMessage(error) {
      const isParseFailure = error && (error.code === 12 || error.name === 'SyntaxError');
      if (isParseFailure) {
        return "We couldn't reach our payment processor. Please check your connection and try again.";
      }
      if (error?.code === 'not_authenticated') {
        return 'This payment form timed out. Please refresh the page and try again.';
      }
      return `Error ${error.code}: ${error.message}`;
    },
    // These failures never reach the server (the tokenize call goes browser -> JustiFi), so this is the
    // only place they can be made visible. Sentry is initialized per pack (shared/sentry_config.js).
    reportJustifiTokenizeFailure(error, stage) {
      const code = String(error?.code ?? 'unknown');
      if (JUSTIFI_BANK_DECLINE_CODES.includes(code)) {
        return;
      }

      const kind = JUSTIFI_CARD_DETAIL_CODES.includes(code) ? 'card_details' : 'platform';
      try {
        Sentry.withScope((scope) => {
          scope.setTag('justifi_tokenize_stage', stage);
          scope.setTag('justifi_failure_kind', kind);
          scope.setTag('justifi_error_code', code);
          // One issue per code, rather than one per message wording
          scope.setFingerprint(['justifi-tokenize-failed', code]);
          scope.setContext('justifi_tokenize', {
            code: error?.code,
            name: error?.name,
            message: error?.message,
            account_id: this.billingAcctId,
            user_agent: navigator.userAgent,
          });
          const level = kind === 'platform' ? 'error' : 'warning';
          Sentry.captureMessage(`JustiFi tokenize failed: ${code} ${error?.message ?? error}`, level);
        });
      } catch (e) {
        // Reporting must never break the checkout flow
        console.warn('Unable to report JustiFi tokenize failure', e);
      }
    },
  },
};
